/**************************************************************************************************
 *
 *  This is the main container for all project_type related views: ProjectType Detail (hierarch), ProjectType
 *  Edit/Creation, Build Edit/Creation, Task Detail, Field Task Detail
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
**************************************************************************************************/

import React from 'react';
import { connect } from 'react-redux';
import BaseComponent from '../../../baseClasses/BaseComponent';
import animator from '../../../helpers/animator';
import dataModel from '../../../models/DataModel';
import ProjectSearchFilter from '../../../components/ProjectsNav/ProjectSearchFilter';

import ProjectTypeNavCard from './ProjectTypeNavCard';
import ProjectTypeAddCard from './ProjectTypeAddCard';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import DataSource from '../../../dao/DataSource/DataSource';
import LoadingDots from '../../../components/LoadingDots';
import eventBus from '../../../helpers/EventBus';
import projectTemplatesService from '../../../services/projectTemplatesService';
import StoreItemProcessing from '../../../helpers/StoreFunctions/StoreItemProcessing';
import { parseProjectTypesFromApiResponse } from './utils/parseProjectTypesFromApi';
import { normalizeProjectTemplateVersions } from './utils/normalizeProjectTemplateVersions';

import AddIcon from '../../../assets/images/add-icon-white.svg';
import './ProjectTypesNav.scss';

import _ from 'lodash';

/**
 * Writes normalized templates (with correct num_versions and latest) into Redux so that
 * components reading from new_project_templates get correct latest-version logic.
 */
function syncProjectTemplatesNumVersionsToStore(projectTypes) {
  if (!projectTypes || !dataModel || !dataModel.store) return;
  projectTypes.forEach(pt => {
    if (!pt.project_templates || !pt.project_templates.length) return;
    pt.project_templates.forEach(template => {
      const existing = StoreItemProcessing.findItem('project_template', template.id);
      const toSet = existing
        ? { ...existing, num_versions: template.num_versions, version: template.version, latest: template.latest }
        : template;
      StoreItemProcessing.setItemInStore('project_template', toSet);
    });
  });
}

const projectTypeDescriptor = { 
  project_type: {
    project_templates: {
      markets: {},
    },
  },
};

const projectTemplateDescriptor = {
  project_template: {
    company: {},
    markets: {},
  },
};

class ProjectTypesNav extends BaseComponent {

  constructor(props) {
    super(props);
    // this.state = { project_types: props.project_types }
    const savedSearch = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('projectTypesNavSearch')) || '';
    this.state = { ...this.state, searchString: savedSearch };
    this.animator = new animator();
    this.project_typeScrollerHeight = 0;
    this.favoriteScrollerHeight = 0;
    this.extraneousHeight = 124;
    this.initializing = true;
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.createProjectTypeCards = this.createProjectTypeCards.bind(this);
    this.mySortedProjectTypes = [];
    this.handleProjectTypeSearch = this.handleProjectTypeSearch.bind(this);
    this.debouncedProjectTypeSearch = _.debounce((filter, searchString) => {
      this.setState({ searchString });
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('projectTypesNavSearch', searchString);
      this.handleProjectTypeSearch(filter, searchString);
    }, 400);
  }

  /**
   * Fetches project types (and templates) directly from the API. This is the source of
   * truth for the nav list; it bypasses Redux so all types/versions show after login.
   */
  fetchProjectTypesFromApi(searchString) {
    const { currentCompany, currentUser } = this.props;
    if (!currentCompany || !currentUser) return;

    const fetchId = (this._projectTypesFetchId = (this._projectTypesFetchId || 0) + 1);
    this.setState({ loadingProjectTypes: true, projectTypesLoadError: null });
    const options = { recordDescriptor: projectTypeDescriptor };
    projectTemplatesService.getProjectTypesByCompany(currentCompany.id, searchString || '', null, options)
      .then(response => {
        if (!this.mounted || this._projectTypesFetchId !== fetchId) return;
        const company = this.props.currentCompany;
        const user = this.props.currentUser;
        if (!company || !user) return;

        if (response && response.included) {
          StoreItemProcessing.syncIncludesToStore(response);
        }
        const projectTypes = parseProjectTypesFromApiResponse(response);
        const filtered = projectTypes.filter(pt =>
          pt.company_id == null || pt.company_id === company.id
        );
        normalizeProjectTemplateVersions(filtered);
        syncProjectTemplatesNumVersionsToStore(filtered);
        this.setState({ loadingProjectTypes: false, projectTypes: filtered, projectTypesLoadError: null });
        if (this.props.handleFinishedLoading) {
          this.props.handleFinishedLoading(filtered ? filtered.length : 0);
        }
      })
      .catch((err) => {
        if (!this.mounted || this._projectTypesFetchId !== fetchId) return;
        this.setState({
          loadingProjectTypes: false,
          projectTypes: [],
          projectTypesLoadError: true,
        });
        if (this.props.handleFinishedLoading) this.props.handleFinishedLoading(0);
        const message = (err && (err.message || err.error)) || 'Request failed';
        if (dataModel && typeof dataModel.displayHTTPError === 'function') {
          dataModel.displayHTTPError('Loading project types', message, false);
        }
      });
  }

  /**
   * Legacy handler for CollectionDataSource. The nav list is driven by fetchProjectTypesFromApi;
   * this is kept for any ActionCable or code paths that still push updates via the collection.
   */
  handleUpdateProjectTypes(projectTypes, action, acData, fetchedData) {
    if (action == 'prefetch') {
      this.setState({ loadingProjectTypes: true });
    }
    else {
      const filteredProjectTypes = projectTypes.filter(projectType => projectType.company_id === this.props.currentCompany.id);
      this.setState({ loadingProjectTypes: false, projectTypes: filteredProjectTypes });
      if (acData) {
        
        if (acData.sub_type == 'copyProjectTemplate' && acData.updating_user_id == this.props.currentUser.id) {
          this.setState({ animateExpand: +acData.project_template_id });
          if (acData.project_template_id) {
            if (!this.state.selectedProjectTemplate || this.state.selectedProjectTemplate.id != +acData.project_template_id) {
              if (this.projectTypeDataSource) {
                this.projectTypeDataSource = null;
              }
              this.projectTemplateDataSource = new DataSource('ProjectTemplate', +acData.project_template_id, projectTemplateDescriptor, this.handleUpdateSelectedProjectTemplate);
              this.projectTemplateDataSource.get();
            }
          }
          if (acData.project_type_id) {
            if (!this.state.selectedProjectType || this.state.selectedProjectType.id != +acData.project_type_id) {
              if (this.projectTypeDataSource) {
                this.projectTypeDataSource = null;
              }
              this.projectTypeDataSource = new DataSource('ProjectType', +acData.project_type_id, projectTypeDescriptor, this.handleUpdateSelectedProjectType);
              this.projectTypeDataSource.get();
            }
          }
        }
      } 
  
      if (this.props.handleFinishedLoading) {
        this.props.handleFinishedLoading(filteredProjectTypes ? filteredProjectTypes.length: 0);
      }
    }
  }

  handleUpdateSelectedProjectType(selectedProjectType, action, acData, fetchedData) {
    if (!selectedProjectType) return;
    this.setState({ selectedProjectType });
    const element = document.getElementById('project_type-' + selectedProjectType.id);
    if (element) {
      this.scrollIntoView(element);
    }
    if (this.projectTemplateDataSource) {
      this.projectTemplateDataSource.get();
    }
  }

  handleUpdateSelectedProjectTemplate(selectedProjectTemplate, action, acData, fetchedData) {
    const { selectedProjectType } = this.state;
    if (selectedProjectType && selectedProjectTemplate && Array.isArray(selectedProjectType.project_templates)) {
      const updated = {
        ...selectedProjectType,
        project_templates: selectedProjectType.project_templates.map(pt =>
          pt.id === selectedProjectTemplate.id ? selectedProjectTemplate : pt,
        ),
      };
      this.setState({ selectedProjectTemplate, selectedProjectType: updated });
    } else {
      this.setState({ selectedProjectTemplate });
    }
  }

  handleUpdateMarkets(markets, action, acData, fetchedData){
    if (markets) {
      this.setState({ markets });
    }
  }

  handleProjectTypeSearch = (filter, searchString) => {
    this.fetchProjectTypesFromApi(searchString);
  }

  handleProjectTypeSearchChange(searchString) {
    const { filter } = this.state;
    this.debouncedProjectTypeSearch(filter, searchString);
  }

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;
    // event for create template - update projectTypes
    eventBus.on('templateVersionCreated', this.handleTemplateVersionCreated);
    eventBus.on('templateCreated', this.handleTemplateCreated);
    eventBus.on('templateDeleted', this.handleTemplateDeletedEvent);
    const { path } = this.props;
    this.setState({ path });
    // Use direct API so types and versions show after login (Redux normalization can drop templates)
    this.fetchProjectTypesFromApi(this.state.searchString);

    // 202 flow: listen for newProjectTemplate so we refresh when server creates template in background
    dataModel.actionCableConnector.registerCallback(
      this.handleNewProjectTemplateFromActionCable,
      ['newProjectTemplate'],
      null,
      null
    );

    this.marketsDataSource = new CollectionDataSource('MarketsCollection', 'Market', { market: {} }, this.handleUpdateMarkets,
      this.props.currentUser.id, this.props.currentCompany.id);
    this.marketsDataSource.current();
  
    if (path.params.project_template_id) {
      this.projectTemplateDataSource = new DataSource('ProjectTemplate', +path.params.project_template_id, projectTemplateDescriptor, this.handleUpdateSelectedProjectTemplate);
      this.projectTemplateDataSource.get();
    }

    if (path.params.project_type_id) {
      this.projectTypeDataSource = new DataSource('ProjectType', +path.params.project_type_id, projectTypeDescriptor, this.handleUpdateSelectedProjectType);
      this.projectTypeDataSource.get();
    }

    document.addEventListener('mousedown', this.handleClickOutside, false);
    if (this.state.selectedProjectType) {
      const element = document.getElementById('project_type-' + this.state.selectedProjectType.id);
      if (element) {
        this.scrollIntoView(element);
      }
    }
  }
  
  componentWillUnmount() {
    this.mounted = false;
    eventBus.off('templateVersionCreated', this.handleTemplateVersionCreated);
    eventBus.off('templateCreated', this.handleTemplateCreated);
    eventBus.off('templateDeleted', this.handleTemplateDeletedEvent);
    dataModel.actionCableConnector.unregisterCallback(
      this.handleNewProjectTemplateFromActionCable,
      ['newProjectTemplate']
    );
    document.removeEventListener('mousedown', this.handleClickOutside, false);
    if (this.marketsDataSource) {
      this.marketsDataSource.release();
      this.marketsDataSource = null;
    }

    if (this.projectTypeDataSource) {
      this.projectTypeDataSource.release();
      this.projectTypeDataSource = null;
    }

    if (this.projectTemplateDataSource) {
      this.projectTemplateDataSource.release();
      this.projectTemplateDataSource = null;
    }
  }

  handleTemplateVersionCreated = () => {
    this.fetchProjectTypesFromApi(this.state.searchString || '');
  }

  handleTemplateDeletedEvent = ({ id, group_identifier, is_template, projectTypeId }) => {
    this.handleDeleteTemplate(id, group_identifier, is_template, projectTypeId);
  }

  handleTemplateCreated = (payload) => {
    const newTemplate = payload && payload.newTemplate;
    if (newTemplate && newTemplate.project_type_id != null && this.state.projectTypes) {
      const projectTypeId = +newTemplate.project_type_id;
      const updated = this.state.projectTypes.map(pt => {
        if (pt.id !== projectTypeId && pt.id != projectTypeId) return pt;
        const templates = Array.isArray(pt.project_templates) ? pt.project_templates : [];
        if (templates.some(t => t.id === newTemplate.id || t.id == newTemplate.id)) return pt;
        const template = { id: newTemplate.id, name: newTemplate.name != null ? newTemplate.name : '', ...newTemplate };
        return { ...pt, project_templates: [...templates, template] };
      });
      this.setState({ projectTypes: updated });
    }
    this.fetchProjectTypesFromApi(this.state.searchString || '');
  }

  /**
   * ActionCable: the server broadcasts 'newProjectTemplate' when a queued version finishes.
   * Refresh the list so the version appears for anyone watching this project type.
   *
   * No toast and no ready-event here. ProjectTemplateHeader owns finishing its own create --
   * it watches the group and selects the new version -- and a second mechanism racing it is
   * what made this flow unreliable: this broadcast never arrives without Redis, so the header
   * could not depend on it, and when it did arrive it announced a template without ever
   * selecting it.
   */
  handleNewProjectTemplateFromActionCable = () => {
    if (!this.mounted) return;
    this.fetchProjectTypesFromApi(this.state.searchString || '');
  }

  handleClickOutside(e) {
    const { displayAddCard } = this.state;
    if (displayAddCard && this.addTypeCardRef && !this.addTypeCardRef.contains(e.target)) {
      this.handleCloseAddCard();
    } 
  }

  scrollIntoView(element) {
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    // remove the element from state when scrolled to
    this.scrollToProjectType = null;
  }

  componentDidUpdate(prevProps) {
    const { path, currentCompany, currentUser } = this.props;
    const { selectedProjectType, projectTypes } = this.state;
    // After login, user/company update; refetch so types and versions load for the new session
    const companyChanged = (currentCompany && prevProps.currentCompany && currentCompany.id !== prevProps.currentCompany.id) ||
      (!prevProps.currentCompany && currentCompany);
    const userChanged = (currentUser && prevProps.currentUser && currentUser.id !== prevProps.currentUser.id) ||
      (!prevProps.currentUser && currentUser);
    if (companyChanged || userChanged) {
      if (currentCompany && currentUser) {
        this.fetchProjectTypesFromApi(this.state.searchString || '');
      }
    }
    if (path.params.project_template_id != prevProps.path.params.project_template_id && path.params.project_template_id) {
      if (this.projectTemplateDataSource) {
        this.projectTemplateDataSource.release();
      }
      this.projectTemplateDataSource = new DataSource('ProjectTemplate', +path.params.project_template_id, projectTemplateDescriptor, this.handleUpdateSelectedProjectTemplate);
      this.projectTemplateDataSource.get();
    }
    if (path.params.project_type_id != prevProps.path.params.project_type_id && path.params.project_type_id) {
      if (this.projectTypeDataSource) {
        this.projectTypeDataSource.release();
      }
      this.projectTypeDataSource = new DataSource('ProjectType', +path.params.project_type_id, projectTypeDescriptor, this.handleUpdateSelectedProjectType);
      this.projectTypeDataSource.get();
    }
    if (!this.state.projectTypes || this.state.projectTypes.length == 0) {
      return;
    }
    if (this.scrollToAddCard) {
      this.scrollToAddCard = false;
      const element = document.getElementById('project_type-add-card');
      if (element) {
        this.scrollIntoView(element);
      }
    }

    const targetTemplateId = path.params['project-template_id'];
    // only run if we have an ID and the specific parent Project Type is selected/expanded
    if (targetTemplateId && this.state.selectedProjectType) {
      const templateElementId = `project-template-nav-${targetTemplateId}`;
      const element = document.getElementById(templateElementId);

      if (element) {
        // 1. Scroll to the specific template
        element.scrollIntoView({ behavior: 'instant', block: 'center' });
        // 2. Click the element
        element.click();
        
        // Prevent the generic ProjectType scrolling below from overriding this
        this.scrollToProjectType = null; 
      }
    }

    if (this.scrollToProjectType) {
      if (!this.initializing) {
        const element = document.getElementById('project-type-nav-' + this.scrollToProjectType.id);
        if (element) {
          this.scrollIntoView(element);
        }
      }
    }
  }
  
  handleDeleteTemplate(id, group_identifier, is_template, projectTypeId){
    if(is_template){
      this.setState(prevState => ({
        projectTypes: prevState.projectTypes.map(projectType => {
          if (projectType.project_templates) {
            projectType.project_templates = projectType.project_templates.filter(template => template.group_identifier !== group_identifier);
          }
          return projectType;
        }),
      }));
    }
    else{
      // Invalidate cache and force a fresh fetch from the server
      this.fetchProjectTypesFromApi(this.state.searchString || '');
    }
    this.props.history.push('/project-types');
  }

  handleUpdateProjectTypeTemplates = (updatedTemplate, projectTypeId) => {
    this.setState(prevState => ({
      projectTypes: prevState.projectTypes.map(projectType =>
        projectType.id === projectTypeId
          ? {
            ...projectType,
            project_templates: projectType.project_templates.map(template =>
                template.id === updatedTemplate.id ? { ...template, ...updatedTemplate } : template
              ),
          }
          : projectType
      ),
    }));
  };

  handleEditProjectType(projectType) {
    const newName = (projectType && projectType.name != null) ? String(projectType.name) : '';
    const withSameName = (this.state.projectTypes || []).filter((existing) => {
      if (existing.id == projectType.id) return false;
      const existingName = (existing.name != null) ? String(existing.name) : '';
      return existingName.toUpperCase() === newName.toUpperCase();
    });
    if (withSameName.length > 0) {
      this.props.dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: this.textMap.projectTypeWithNameAlreadyExists,
        height: 170,
        width: 300,
        okButtonText: this.textMap.okButtonText,
        oneButton: true,
      });
    } else {
      dataModel.projectTypesDAO.updateProjectType(projectType);
    }
  }
  splitByDigitsAndNonDigits(str) {
    return str.match(/\d+|\D+/g);
  };

  compareSegments(a, b) {
    const sa = (a != null && typeof a === 'string') ? a : '';
    const sb = (b != null && typeof b === 'string') ? b : '';
    const segmentsA = this.splitByDigitsAndNonDigits(sa.toLowerCase()) || [];
    const segmentsB = this.splitByDigitsAndNonDigits(sb.toLowerCase()) || [];

    const len = Math.min(segmentsA.length, segmentsB.length);

    for (let i = 0; i < len; i++) {
      const segmentA = segmentsA[i];
      const segmentB = segmentsB[i];

      if (segmentA !== segmentB) {
        const numA = parseInt(segmentA, 10);
        const numB = parseInt(segmentB, 10);

        // compare numerically
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        // compare lexically
        return segmentA.localeCompare(segmentB);
      }
    }

    return segmentsA.length - segmentsB.length;
  };

  createProjectTypeCards() {
    let actualProjectTypes = this.state.projectTypes ? this.state.projectTypes : [];
    const animateExpand = this.props.animateExpand;
    //Don't allow duplicates
    let reduceDuplicates = actualProjectTypes.reduce((accumulator, element)=>{
      if(!accumulator.find(data => data.id == element.id)){
        accumulator.push(element);
      }
      return accumulator;
    }, []);
    //Remove deleted item and sort project types before mapping them
    const sortedProjectTypeCards = reduceDuplicates
      .filter(pt => pt.deleted !== true)
      .sort((a, b) => this.compareSegments(a && a.name, b && b.name));
    //Pass sorted array this to ProjecTypeAddCard
    this.mySortedProjectTypes = sortedProjectTypeCards;
    this.project_typeScrollerHeight = 0;
    const project_typeCards = !sortedProjectTypeCards ? [] : sortedProjectTypeCards.map(project_type => {
      this.project_typeScrollerHeight += 71;
      const selected = this.props.selectedProjectType ? this.props.selectedProjectType.id == project_type.id : false;
      if (selected) {
        if (!this.initializing) {
          this.scrollToProjectType = project_type;
        }
      }
      return (
        <ProjectTypeNavCard 
          id={'project-type-nav-' + project_type.id} 
          key={'project-type-nav-' + project_type.id} 
          project_type={project_type}
          company={this.props.selectedCompany}
          selectedProjectType={this.state.selectedProjectType}
          selectedProjectTemplate={this.state.selectedProjectTemplate}
          selectedCompany={this.props.currentCompany}
          editProjectType={this.handleEditProjectType} 
          selected={selected}
          closeoutTemplates={this.props.closeoutTemplates}
          markets={this.state.markets}
          handleDeleteType={this.props.handleDeleteType}
          handleAddProjectTemplate={this.props.handleAddProjectTemplate}
          animateExpand={selected && animateExpand }
          allTypes={this.state.projectTypes}
          handleDeleteTemplate={this.handleDeleteTemplate}
          handleUpdateProjectTypeTemplates={this.handleUpdateProjectTypeTemplates}
          textMap={this.textMap}
          />
      );
    });

    return project_typeCards;
  }

  handleDisplayAddCard() {
    this.setState({ displayAddCard: true });
  }

  handleCloseAddCard() {
    this.setState({ displayAddCard: false });
    if (this.props.clearButton) {
      this.props.clearButton();
    }
  }

  handleProjectTypeAdd(projectType) {
    this.scrollToProjectType = projectType;
    if (this.scroller && this.scroller.scrollTop !== undefined) {
      this.scroller.scrollTop = 0;
    }
    this.fetchProjectTypesFromApi(this.state.searchString || '');
  }

  render() {
    const { loadingProjectTypes } = this.state;
    const project_typeNavCards = this.createProjectTypeCards();
    if (!this.props.permissions) {
      return null;
    }

    this.initializing = false;

    const project_typeScrollerStyle = { height: window.innerHeight - this.extraneousHeight - Math.min(this.favoriteScrollerHeight, 164) };

    let project_typeAddCard = null;
    if (this.state.displayAddCard) {
      project_typeAddCard = 
        <div ref={el => this.addTypeCardRef = el}>
        <ProjectTypeAddCard 
          id="project_type-add-card" 
          closeAddCard={this.handleCloseAddCard} 
          handleProjectTypeAdd={this.handleProjectTypeAdd} 
          selectedCompany={this.props.selectedCompany} 
          allActualProjecTypes={this.mySortedProjectTypes}
        />
        </div>;
      this.scrollToAddCard = true;
    }

    let addButton = null;
    if (!this.state.displayAddCard && !this.props.currentCompany.admin_company && !this.props.addButton) {
      if (this.props.permissions.canCreateProjectType) {
        addButton = <img className="add-button" src={AddIcon} alt="add"
          onClick={this.handleDisplayAddCard} />;
      }
    }

    let project_typesLabel = this.textMap.projectTypes;
    let underline = null;
    if (project_typeNavCards.length > 0) {
      underline = <hr className="section-header-underline"/>;
    }

    let footerCards = null;
    let noneFound = null;

    const projectTypes = this.state.projectTypes;
    const projectTypesLoadError = this.state.projectTypesLoadError;
    if (projectTypesLoadError) {
      noneFound = <div className="no-matches-indicator">
        Failed to load project types. Please try again.
      </div>;
    } else if (projectTypes) {
      if (projectTypes.length === 0) {
        noneFound = <div className="no-matches-indicator">
          No Results Found...
        </div>;
      }
    }  

    let searchFilter =
          <ProjectSearchFilter
            hideFilter={true}
            handleSearchChange={this.handleProjectTypeSearchChange}
            search={this.state.searchString}
            resetSearchString={this.state.resetSearchString}
            overridePlaceHolder={this.textMap.searchProjectTypes}
          />;

    return (
      <div className={'project_types-nav'}>
        <div className="project_types">
          <div className="section-header">{project_typesLabel.toUpperCase()}{addButton}</div>
          {searchFilter}
          <div id="project_types-scroller" className="project_types-scroller"
            ref={(element) => { this.scroller = element; }}>
            <div className="project_types-list" style={project_typeScrollerStyle} ref={(element) => { this.list = element;}}>
              {project_typeAddCard}
              {underline}
              {loadingProjectTypes && <LoadingDots/>}
              {project_typeNavCards}
              {footerCards}
              {noneFound}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {

  return {
    dataModel: dataModel,
    
    permissions: state.new_users ? state.new_users.permissions : null,
    currentUser: state.new_users ? state.new_users.currentUser : null,
    currentCompany: state.new_companies ? state.new_companies.currentCompany : null,
  };
};

export default connect(mapStateToProps)(ProjectTypesNav);



// WEBPACK FOOTER //
// ./src/containers/ProjectTemplatesContainer/ProjectTypesNav/index.js